// API base — prefer the origin the app is served from (same-origin) so a
// browser at https://lunoras.in never sends credentialed /api requests to a
// different domain. Falls back to the build-time env var only when there is
// no browser (SSR, tests, tooling). Kate at 3DLOOK (2026-09-26) and deployer
// RCA 9930cd40 both traced the post-scan loop to this bake-time cross-origin
// baseline: cookies stored on lunoras.in did not survive the emergent.host
// hop, so Layer A recover-scan silently 401'd and the flow reset to Welcome.
const API =
  (typeof window !== 'undefined' && window.location && window.location.origin) ||
  process.env.REACT_APP_BACKEND_URL;

function formatError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

async function request(path, options = {}) {
  const { silent, ...fetchOpts } = options;
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...fetchOpts.headers },
    ...fetchOpts,
  });
  if (!res.ok) {
    if (silent) {
      const err = new Error("silent");
      err.status = res.status;
      throw err;
    }
    const data = await res.json().catch(() => ({}));
    const err = new Error(formatError(data.detail));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Auth
export const authAPI = {
  register: (data) => request("/api/auth/register", { method: "POST", body: JSON.stringify(data) }),
  registerTailor: (data) => request("/api/auth/register-tailor", { method: "POST", body: JSON.stringify(data) }),
  login: (data) => request("/api/auth/login", { method: "POST", body: JSON.stringify(data) }),
  loginPhone: (data) => request("/api/auth/login-phone", { method: "POST", body: JSON.stringify(data) }),
  logout: () => request("/api/auth/logout", { method: "POST" }),
  me: () => request("/api/auth/me", { silent: true }),
  refresh: () => request("/api/auth/refresh", { method: "POST" }),
  forgotPassword: (data) => request("/api/auth/forgot-password", { method: "POST", body: JSON.stringify(data) }),
  resetPassword: (data) => request("/api/auth/reset-password", { method: "POST", body: JSON.stringify(data) }),
};

// Orders
export const ordersAPI = {
  create: (data) => request("/api/orders/", { method: "POST", body: JSON.stringify(data) }),
  list: (status) => request(`/api/orders/${status ? `?status=${status}` : ""}`),
  get: (orderId) => request(`/api/orders/${orderId}`),
  update: (orderId, data) => request(`/api/orders/${orderId}`, { method: "PATCH", body: JSON.stringify(data) }),
  cancel: (orderId, reason) => request(`/api/orders/${orderId}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),
  all: (status) => request(`/api/orders/all${status ? `?status=${status}` : ""}`),
};

// Tailor
export const tailorAPI = {
  dashboard: () => request("/api/tailor/dashboard"),
  toggleAvailability: () => request("/api/tailor/availability", { method: "PATCH" }),
  addStaff: (data) => request("/api/tailor/staff", { method: "POST", body: JSON.stringify(data) }),
  getStaff: () => request("/api/tailor/staff"),
  customerHistory: (customerId) => request(`/api/tailor/customer-history/${customerId}`),
  saveAlteration: (orderId, data) => request(`/api/tailor/alteration-memory/${orderId}`, {
    method: "POST", body: JSON.stringify(data),
  }),
};

// Body scan (3DLOOK / mocked provider)
export const bodyProfileAPI = {
  current: () => request("/api/body-profile/current"),
  history: (limit = 20) => request(`/api/body-profile/history?limit=${limit}`),
  activate: (profileId) => request(`/api/body-profile/${profileId}/activate`, { method: "POST" }),
  remove: (profileId) => request(`/api/body-profile/${profileId}`, { method: "DELETE" }),
  save: (payload) => request("/api/body-profile", { method: "POST", body: JSON.stringify(payload) }),
  fromWidget: (payload) => request("/api/body-profile/from-widget", { method: "POST", body: JSON.stringify(payload) }),
  me: () => request("/api/body-profile/me"),
  startScanSession: () => request("/api/body-profile/scan-session/start", { method: "POST" }),
  recoverScan: (payload) => request("/api/body-profile/recover-scan", { method: "POST", body: JSON.stringify(payload || {}) }),
};

export const scanAPI = {
  initiate: (data) => request("/api/scans/initiate", { method: "POST", body: JSON.stringify(data) }),
  status: (scanId) => request(`/api/scans/${scanId}`),
  completeMock: (scanId) => request(`/api/scans/${scanId}/complete-mock`, { method: "POST" }),
};

// Admin
export const adminAPI = {
  stats: () => request("/api/admin/stats"),
  customers: () => request("/api/admin/customers"),
  tailors: () => request("/api/admin/tailors"),
  deleteUser: (id) => request(`/api/admin/users/${id}`, { method: "DELETE" }),
};

// Quality Control
export const qcAPI = {
  // Tailor
  start: (orderId) => request(`/api/qc/orders/${orderId}/start`, { method: "POST" }),
  submit: (orderId, formData) => fetch(`${API}/api/qc/orders/${orderId}/submit`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(async r => {
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      const err = new Error(formatError(d.detail));
      err.status = r.status;
      throw err;
    }
    return r.json();
  }),
  dispatch: (orderId) => request(`/api/qc/orders/${orderId}/dispatch`, { method: "POST" }),
  // Admin / Founder
  adminList: (filter = 'all') => request(`/api/admin/qc?filter=${filter}`),
  adminDetail: (orderId) => request(`/api/admin/qc/order/${orderId}`),
  tailorStats: () => request("/api/admin/qc/tailor-stats"),
};

// Error Monitoring Center
export const errorsAPI = {
  summary: () => request("/api/admin/errors/summary"),
  list: (filter = 'all', category = null) => {
    const params = new URLSearchParams();
    if (filter) params.set('filter', filter);
    if (category) params.set('category', category);
    return request(`/api/admin/errors?${params.toString()}`);
  },
  detail: (id) => request(`/api/admin/errors/${id}`),
  updateStatus: (id, status, note = '') => request(`/api/admin/errors/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status, note }),
  }),
  reports: (days = 14) => request(`/api/admin/errors/reports/overview?days=${days}`),
};

// Founder-only Backup Center
export const backupAPI = {
  summary: () => request("/api/founder/backups/summary"),
  list: (limit = 100) => request(`/api/founder/backups?limit=${limit}`),
  run: () => request("/api/founder/backups/run", { method: "POST" }),
  remove: (id) => request(`/api/founder/backups/${id}`, { method: "DELETE" }),
  restore: (id) => request(`/api/founder/backups/${id}/restore`, {
    method: "POST",
    body: JSON.stringify({ confirm: "RESTORE" }),
  }),
  downloadUrl: (id) => `${API}/api/founder/backups/${id}/download`,
};

// Category Media Manager
export const mediaAPI = {
  // Admin
  upload: (formData) => fetch(`${API}/api/admin/media/upload`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(async r => {
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(formatError(d.detail)); }
    return r.json();
  }),
  list: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
    return request(`/api/admin/media${qs ? `?${qs}` : ''}`);
  },
  update: (id, data) => request(`/api/admin/media/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  reorder: (items) => request(`/api/admin/media/reorder`, { method: "POST", body: JSON.stringify({ items }) }),
  remove: (id) => request(`/api/admin/media/${id}`, { method: "DELETE" }),
  configStatus: () => request(`/api/admin/media/config-status`),
  // Public
  publicList: (category, subcategory) => {
    const qs = new URLSearchParams({ category, ...(subcategory ? { subcategory } : {}) }).toString();
    return request(`/api/catalog/media?${qs}`);
  },
};

// Wishlist
export const wishlistAPI = {
  list: () => request("/api/wishlist"),
  add: (product) => request("/api/wishlist", { method: "POST", body: JSON.stringify(product) }),
  remove: (productId) => request(`/api/wishlist/${encodeURIComponent(productId)}`, { method: "DELETE" }),
};

// Referrals
export const referralsAPI = {
  publicSettings: () => request("/api/referrals/public-settings"),
  me: () => request("/api/referrals/me"),
  history: () => request("/api/referrals/history"),
  applyCoupon: (code, orderTotal) => request("/api/referrals/apply-coupon", { method: "POST", body: JSON.stringify({ code, order_total: orderTotal }) }),
  adminSettings: () => request("/api/referrals/admin/settings"),
  updateAdminSettings: (data) => request("/api/referrals/admin/settings", { method: "PATCH", body: JSON.stringify(data) }),
  adminStats: () => request("/api/referrals/admin/stats"),
};

// Measurements (Fusion Engine)
export const measurementsAPI = {
  me: () => request("/api/measurements/me"),
  save: (category, measurements) => request("/api/measurements/me", { method: "PUT", body: JSON.stringify({ category, measurements }) }),
  delete: (schemaKey) => request(`/api/measurements/me/${schemaKey}`, { method: "DELETE" }),
  fuse: (data) => request("/api/measurements/fuse", { method: "POST", body: JSON.stringify(data) }),
  feedback: (data) => request("/api/measurements/feedback", { method: "POST", body: JSON.stringify(data) }),
};

// Recommendations
export const recsAPI = {
  trackEvent: (data) => request("/api/recommendations/events", { method: "POST", body: JSON.stringify(data) }),
  me: (limit = 8) => request(`/api/recommendations/me?limit=${limit}`),
  recentlyViewed: (limit = 12) => request(`/api/recommendations/recently-viewed?limit=${limit}`),
  similar: (productId, limit = 8) => request(`/api/recommendations/similar/${encodeURIComponent(productId)}?limit=${limit}`),
  trending: (days = 7, limit = 12) => request(`/api/recommendations/trending?days=${days}&limit=${limit}`),
  adminAnalytics: (days = 30) => request(`/api/recommendations/admin/analytics?days=${days}`),
};

// Backend base URL exposed for <img src> composition
export const BACKEND_URL = API;
